'use client';

import { createContext, useContext, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

interface RTVToastContextType {}

const RTVToastContext = createContext<RTVToastContextType>({});

export const useRTVToast = () => useContext(RTVToastContext);

const stageLabels: Record<string, string> = {
  pmi_request: 'Iniciado',
  cgs_cs_review: 'Recolección',
  cg_ops_approval: 'Validación',
  cedis_receiving: 'Resguardo',
  cgs_whse_processing: 'En Tránsito',
  planta_pmi_return: 'Recibido Planta',
  completed: 'Completado',
  cancelled: 'Cancelado',
};

function getStageLabel(stage: string | null | undefined): string {
  if (!stage) return '—';
  return stageLabels[stage] || stage;
}

export function RTVToastProvider({ children }: { children: React.ReactNode }) {
  const { user, profile } = useAuth();
  // Track previous stage per RTV to detect stage advances
  const prevStagesRef = useRef<Record<string, string>>({});

  useEffect(() => {
    if (!user || !profile) return;

    const supabase = createClient();

    // Seed initial stages so we can detect changes
    const seedStages = async () => {
      try {
        const query = supabase
          .from('rtvs')
          .select('id, current_stage, company_id');

        // Filter by company if not super_admin
        const { data } = profile.role === 'super_admin'
          ? await query
          : await query.eq('company_id', (profile as any).company_id);

        if (data) {
          data.forEach((rtv: { id: string; current_stage: string }) => {
            prevStagesRef.current[rtv.id] = rtv.current_stage;
          });
        }
      } catch {
        // silent
      }
    };

    seedStages();

    const channel = supabase
      .channel('rtv-toast-global')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'rtvs',
        },
        (payload) => {
          const rtv = payload.new as any;
          // Only show if belongs to user's company (or super_admin)
          if (
            profile.role !== 'super_admin' &&
            rtv.company_id !== (profile as any).company_id
          ) return;

          prevStagesRef.current[rtv.id] = rtv.current_stage;

          toast.success('Nueva RTV creada', {
            description: `Folio ${rtv.folio || rtv.id} — Etapa: ${getStageLabel(rtv.current_stage)}`,
            duration: 5000,
            icon: '📦',
          });
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'rtvs',
        },
        (payload) => {
          const rtv = payload.new as any;
          const oldRtv = payload.old as any;

          // Only show if belongs to user's company (or super_admin)
          if (
            profile.role !== 'super_admin' &&
            rtv.company_id !== (profile as any).company_id
          ) return;

          const prevStage = prevStagesRef.current[rtv.id] ?? oldRtv?.current_stage;
          const newStage = rtv.current_stage;

          // Stage advance
          if (prevStage && newStage && prevStage !== newStage) {
            prevStagesRef.current[rtv.id] = newStage;

            if (newStage === 'completed') {
              toast.success('RTV completada', {
                description: `Folio ${rtv.folio || rtv.id} ha sido completada exitosamente.`,
                duration: 6000,
                icon: '✅',
              });
            } else if (newStage === 'cancelled') {
              toast.error('RTV cancelada', {
                description: `Folio ${rtv.folio || rtv.id} fue cancelada.`,
                duration: 6000,
                icon: '🚫',
              });
            } else {
              toast.info('Avance de etapa', {
                description: `Folio ${rtv.folio || rtv.id}: ${getStageLabel(prevStage)} → ${getStageLabel(newStage)}`,
                duration: 5000,
                icon: '🔄',
              });
            }
            return;
          }

          // General update (no stage change)
          prevStagesRef.current[rtv.id] = newStage;
          toast('RTV actualizada', {
            description: `Folio ${rtv.folio || rtv.id} fue modificada.`,
            duration: 4000,
            icon: '✏️',
          });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, profile]);

  return (
    <RTVToastContext.Provider value={{}}>
      {children}
    </RTVToastContext.Provider>
  );
}
